/**
 * Performance Monitoring Module
 *
 * Tracks Core Web Vitals and sends metrics to Sentry for analysis.
 * Implements performance measurement for:
 * - LCP (Largest Contentful Paint)
 * - FID (First Input Delay)
 * - CLS (Cumulative Layout Shift)
 * - TTFB (Time to First Byte)
 * - INP (Interaction to Next Paint)
 *
 * @module client/src/lib/monitoring
 */

import * as Sentry from '@sentry/react';

// Types for Core Web Vitals
export interface WebVitalMetric {
  name: 'LCP' | 'FID' | 'CLS' | 'TTFB' | 'INP' | 'FCP';
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
  navigationType: string;
}

export interface PerformanceMetrics {
  lcp: number | null;
  fid: number | null;
  cls: number | null;
  ttfb: number | null;
  inp: number | null;
  fcp: number | null;
}

// Thresholds for Web Vitals ratings (based on Google's recommendations)
const THRESHOLDS = {
  LCP: { good: 2500, poor: 4000 },
  FID: { good: 100, poor: 300 },
  CLS: { good: 0.1, poor: 0.25 },
  TTFB: { good: 800, poor: 1800 },
  INP: { good: 200, poor: 500 },
  FCP: { good: 1800, poor: 3000 },
};

// Storage for collected metrics
let collectedMetrics: PerformanceMetrics = {
  lcp: null,
  fid: null,
  cls: null,
  ttfb: null,
  inp: null,
  fcp: null,
};

/**
 * Get rating for a metric value
 */
function getRating(
  name: keyof typeof THRESHOLDS,
  value: number
): 'good' | 'needs-improvement' | 'poor' {
  const threshold = THRESHOLDS[name];
  if (value <= threshold.good) return 'good';
  if (value <= threshold.poor) return 'needs-improvement';
  return 'poor';
}

/**
 * Send metric to Sentry
 */
function sendToSentry(metric: WebVitalMetric): void {
  // Add breadcrumb for the metric
  Sentry.addBreadcrumb({
    category: 'web-vital',
    message: `${metric.name}: ${metric.value.toFixed(2)} (${metric.rating})`,
    level: metric.rating === 'poor' ? 'warning' : 'info',
    data: {
      name: metric.name,
      value: metric.value,
      rating: metric.rating,
      delta: metric.delta,
    },
  });

  // Set context for performance monitoring
  Sentry.setContext('web_vitals', {
    ...collectedMetrics,
    lastMetric: metric.name,
    lastValue: metric.value,
    lastRating: metric.rating,
  });

  // Create a transaction for poor metrics
  if (metric.rating === 'poor') {
    Sentry.captureMessage(`Poor ${metric.name} detected: ${metric.value.toFixed(2)}`, {
      level: 'warning',
      tags: {
        metric: metric.name,
        rating: metric.rating,
        navigationType: metric.navigationType,
      },
      extra: {
        value: metric.value,
        delta: metric.delta,
        threshold: THRESHOLDS[metric.name as keyof typeof THRESHOLDS],
      },
    });
  }
}

/**
 * Report a web vital metric
 */
export function reportWebVital(metric: WebVitalMetric): void {
  // Store the metric
  const key = metric.name.toLowerCase() as keyof PerformanceMetrics;
  if (key in collectedMetrics) {
    collectedMetrics[key] = metric.value;
  }

  // Send to Sentry
  sendToSentry(metric);

  // Log in development
  if (import.meta.env.DEV) {
    console.log(
      `[Web Vital] ${metric.name}: ${metric.value.toFixed(2)} (${metric.rating})`
    );
  }
}

/**
 * Observer for Largest Contentful Paint
 */
function observeLCP(): void {
  if (!('PerformanceObserver' in window)) return;

  try {
    const observer = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      const lastEntry = entries[entries.length - 1] as PerformanceEntry & {
        startTime: number;
      };

      if (lastEntry) {
        const value = lastEntry.startTime;
        reportWebVital({
          name: 'LCP',
          value,
          rating: getRating('LCP', value),
          delta: value,
          id: `lcp-${Date.now()}`,
          navigationType: getNavigationType(),
        });
      }
    });

    observer.observe({ type: 'largest-contentful-paint', buffered: true });
  } catch (e) {
    console.warn('[Monitoring] LCP observer not supported');
  }
}

/**
 * Observer for First Input Delay
 */
function observeFID(): void {
  if (!('PerformanceObserver' in window)) return;

  try {
    const observer = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      const firstEntry = entries[0] as PerformanceEventTiming;

      if (firstEntry) {
        const value = firstEntry.processingStart - firstEntry.startTime;
        reportWebVital({
          name: 'FID',
          value,
          rating: getRating('FID', value),
          delta: value,
          id: `fid-${Date.now()}`,
          navigationType: getNavigationType(),
        });
      }
    });

    observer.observe({ type: 'first-input', buffered: true });
  } catch (e) {
    console.warn('[Monitoring] FID observer not supported');
  }
}

/**
 * Observer for Cumulative Layout Shift
 */
function observeCLS(): void {
  if (!('PerformanceObserver' in window)) return;

  let clsValue = 0;
  let sessionValue = 0;
  let sessionEntries: PerformanceEntry[] = [];

  try {
    const observer = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries() as (PerformanceEntry & {
        hadRecentInput: boolean;
        value: number;
      })[];

      for (const entry of entries) {
        if (!entry.hadRecentInput) {
          const firstSessionEntry = sessionEntries[0] as
            | (PerformanceEntry & { startTime: number })
            | undefined;
          const lastSessionEntry = sessionEntries[sessionEntries.length - 1] as
            | (PerformanceEntry & { startTime: number })
            | undefined;

          // Start a new session if gap > 1s or session > 5s
          if (
            sessionValue &&
            (entry.startTime - (lastSessionEntry?.startTime || 0) > 1000 ||
              entry.startTime - (firstSessionEntry?.startTime || 0) > 5000)
          ) {
            if (sessionValue > clsValue) {
              clsValue = sessionValue;
            }
            sessionValue = 0;
            sessionEntries = [];
          }

          sessionEntries.push(entry);
          sessionValue += entry.value;
        }
      }

      // Update if this session is the largest
      if (sessionValue > clsValue) {
        clsValue = sessionValue;
        reportWebVital({
          name: 'CLS',
          value: clsValue,
          rating: getRating('CLS', clsValue),
          delta: clsValue,
          id: `cls-${Date.now()}`,
          navigationType: getNavigationType(),
        });
      }
    });

    observer.observe({ type: 'layout-shift', buffered: true });
  } catch (e) {
    console.warn('[Monitoring] CLS observer not supported');
  }
}

/**
 * Observer for Time to First Byte
 */
function observeTTFB(): void {
  if (!('PerformanceObserver' in window)) return;

  try {
    const observer = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      const navigationEntry = entries[0] as PerformanceNavigationTiming;

      if (navigationEntry) {
        const value = navigationEntry.responseStart - navigationEntry.requestStart;
        reportWebVital({
          name: 'TTFB',
          value,
          rating: getRating('TTFB', value),
          delta: value,
          id: `ttfb-${Date.now()}`,
          navigationType: getNavigationType(),
        });
      }
    });

    observer.observe({ type: 'navigation', buffered: true });
  } catch (e) {
    console.warn('[Monitoring] TTFB observer not supported');
  }
}

/**
 * Observer for Interaction to Next Paint
 */
function observeINP(): void {
  if (!('PerformanceObserver' in window)) return;

  let maxINP = 0;

  try {
    const observer = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries() as (PerformanceEntry & {
        duration: number;
        interactionId: number;
      })[];

      for (const entry of entries) {
        if (entry.interactionId && entry.duration > maxINP) {
          maxINP = entry.duration;
          reportWebVital({
            name: 'INP',
            value: maxINP,
            rating: getRating('INP', maxINP),
            delta: entry.duration,
            id: `inp-${Date.now()}`,
            navigationType: getNavigationType(),
          });
        }
      }
    });

    // `durationThreshold` is valid for the 'event' entry type per the Event
    // Timing spec, but is absent from TS's PerformanceObserverInit lib type.
    observer.observe({
      type: 'event',
      buffered: true,
      durationThreshold: 16,
    } as PerformanceObserverInit);
  } catch (e) {
    console.warn('[Monitoring] INP observer not supported');
  }
}

/**
 * Observer for First Contentful Paint
 */
function observeFCP(): void {
  if (!('PerformanceObserver' in window)) return;

  try {
    const observer = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      const fcpEntry = entries.find((e) => e.name === 'first-contentful-paint');

      if (fcpEntry) {
        const value = fcpEntry.startTime;
        reportWebVital({
          name: 'FCP',
          value,
          rating: getRating('FCP', value),
          delta: value,
          id: `fcp-${Date.now()}`,
          navigationType: getNavigationType(),
        });
      }
    });

    observer.observe({ type: 'paint', buffered: true });
  } catch (e) {
    console.warn('[Monitoring] FCP observer not supported');
  }
}

/**
 * Get the navigation type
 */
function getNavigationType(): string {
  if (typeof window === 'undefined') return 'unknown';

  const navigation = window.performance?.getEntriesByType?.(
    'navigation'
  )[0] as PerformanceNavigationTiming;

  return navigation?.type || 'navigate';
}

/**
 * Initialize all performance observers
 */
export function initPerformanceMonitoring(): void {
  if (typeof window === 'undefined') return;

  // Wait for the page to be interactive
  if (document.readyState === 'complete') {
    startObservers();
  } else {
    window.addEventListener('load', startObservers);
  }
}

function startObservers(): void {
  observeLCP();
  observeFID();
  observeCLS();
  observeTTFB();
  observeINP();
  observeFCP();

  console.info('[Monitoring] Performance monitoring initialized');
}

/**
 * Get all collected metrics
 */
export function getCollectedMetrics(): PerformanceMetrics {
  return { ...collectedMetrics };
}

/**
 * Clear collected metrics (useful for SPA navigation)
 */
export function clearMetrics(): void {
  collectedMetrics = {
    lcp: null,
    fid: null,
    cls: null,
    ttfb: null,
    inp: null,
    fcp: null,
  };
}

/**
 * Track custom performance metric
 */
export function trackCustomMetric(
  name: string,
  value: number,
  unit: string = 'ms'
): void {
  Sentry.addBreadcrumb({
    category: 'performance',
    message: `${name}: ${value}${unit}`,
    level: 'info',
    data: { name, value, unit },
  });

  if (import.meta.env.DEV) {
    console.log(`[Performance] ${name}: ${value}${unit}`);
  }
}

/**
 * Measure execution time of a function
 */
export async function measureAsync<T>(
  name: string,
  fn: () => Promise<T>
): Promise<T> {
  const start = performance.now();
  try {
    const result = await fn();
    const duration = performance.now() - start;
    trackCustomMetric(name, duration);
    return result;
  } catch (error) {
    const duration = performance.now() - start;
    trackCustomMetric(`${name}_error`, duration);
    throw error;
  }
}

/**
 * Measure execution time of a sync function
 */
export function measureSync<T>(name: string, fn: () => T): T {
  const start = performance.now();
  try {
    const result = fn();
    const duration = performance.now() - start;
    trackCustomMetric(name, duration);
    return result;
  } catch (error) {
    const duration = performance.now() - start;
    trackCustomMetric(`${name}_error`, duration);
    throw error;
  }
}

export default {
  initPerformanceMonitoring,
  reportWebVital,
  getCollectedMetrics,
  clearMetrics,
  trackCustomMetric,
  measureAsync,
  measureSync,
};
